import { useCallback, useEffect, useMemo, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useLocalStorage } from '../lib/useLocalStorage';
import environment from '../environment';
import { loadWithAuthGuard } from '../authentication/authErrorHandler';
import { TokenStoreContextValue } from '../authentication/TokenStore';

const { ADMINIFI_URL } = environment;

interface RuleSet {
  name: string;
  active: boolean;
  label?: string;
  role?: string;
  interstitial_redirect?: string;
}

export interface UserData {
  id: string;
  impersonator_id?: string;
  flippers: Record<string, boolean>;
  rule_sets: RuleSet[];
  [key: string]: unknown;
}

interface UseUserDataParams {
  slug: string | undefined;
  tokenStore: TokenStoreContextValue;
  userMeUrl: string;
  signOutUrl: string;
}

/**
 * Fetches the current user and normalizes rule_sets.
 * Platform-specific behavior (URL, normalization) is delegated to the platform adapter.
 */
export const useUserData = ({ slug, tokenStore, userMeUrl, signOutUrl }: UseUserDataParams) => {
  const { t } = useTranslation('layout');
  const [searchParams] = useSearchParams();
  const [userData, setUserData] = useState<UserData | undefined>(undefined);
  const currentRuleSetParam = searchParams.get('current_rule_set_name');
  const [currentRuleSetName, setCurrentRuleSetName] = useLocalStorage('current_rule_set_name');

  // Sync search param to localStorage when it changes
  useEffect(() => {
    if (currentRuleSetParam && currentRuleSetParam !== currentRuleSetName) {
      setCurrentRuleSetName(currentRuleSetParam);
    }
  }, [currentRuleSetParam, currentRuleSetName, setCurrentRuleSetName]);

  const markActiveRuleSet = useCallback(
    (data: UserData | undefined): UserData | undefined => {
      if (!slug || !data?.rule_sets) return data;
      return {
        ...data,
        rule_sets: data.rule_sets.map(ruleSet => ({
          ...ruleSet,
          active: ruleSet.name === currentRuleSetName,
          interstitial_redirect: `${ADMINIFI_URL}/${slug}/cportal/interstitial_redirect?current_rule_set_name=${ruleSet.name}`,
        })),
      };
    },
    [currentRuleSetName, slug]
  );

  // Derive active rule set without mutating state
  const derivedUserData = useMemo(() => markActiveRuleSet(userData), [userData, markActiveRuleSet]);

  useEffect(() => {
    async function fetchUser() {
      const user = await loadWithAuthGuard<UserData>(userMeUrl, tokenStore, signOutUrl);
      if (!user) return;
      if (!Array.isArray(user.rule_sets)) {
        user.rule_sets = [
          {
            name: user.rule_sets as unknown as string,
            active: true,
            label: t('Role'),
            role: user.rule_sets as unknown as string,
          },
        ];
      }
      setUserData(user);
      datadogRum.setUser({ id: user.id });
    }

    fetchUser();
  }, [tokenStore, setUserData, signOutUrl, slug, t, userMeUrl]);

  return { currentRuleSetName, userData: derivedUserData };
};
